/**
 * src/components/funnel/primitives.jsx
 *
 * The small shared vocabulary the funnel pages are built from.
 *
 * These are deliberately thin. The design system is about eighty tokens and a
 * handful of rules; the components exist so those rules are applied
 * consistently rather than retyped, not to add a layer of abstraction. If a
 * component here starts growing variants, that is a sign the page wants
 * something the system does not have.
 *
 * The rules being enforced:
 *   - one accent, and at most one saturated moment per page
 *   - depth from light and hairlines, never drop shadows
 *   - huge tight headlines against tiny wide micro-labels
 *   - everything fades up on entry (see lib/useReveal.js)
 */

import React from "react";
import { Link } from "react-router-dom";

/** Joins class names, dropping falsy entries. */
export function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

/**
 * Page-width wrapper. 1200px, 32px gutters.
 */
export function Wrap({ className, children, ...rest }) {
  return (
    <div className={cx("mx-auto w-full max-w-[1200px] px-6 md:px-8", className)} {...rest}>
      {children}
    </div>
  );
}

/**
 * A vertical section. `alt` puts the surface tone behind it; `backlight`
 * bleeds the soft radial from the top edge.
 *
 * The system says at most one surface tone above the canvas and no section
 * colour changes beyond that, which is why there is no `tone` prop.
 */
export function Section({ id, alt = false, backlight = false, className, children }) {
  return (
    <section
      id={id}
      className={cx(
        "relative py-16 md:py-24 lg:py-32",
        // LAKA distinguishes canvas / surface / raised. An alternating
        // section is SURFACE (ink-800); cards sit on RAISED (ink-700)
        // above it. Using raised for both flattened the hierarchy.
        alt && "bg-ink-800",
        className,
      )}
    >
      {backlight && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: "var(--backlight-top)" }}
        />
      )}
      <div className="relative">{children}</div>
    </section>
  );
}

/** Tiny + wide. The counterweight to the headline. */
export function Eyebrow({ className, children }) {
  return <p className={cx("rp-eyebrow m-0", className)}>{children}</p>;
}

/**
 * A section opener: eyebrow, headline, optional lede.
 *
 * Left-aligned by default because the system has one strong left rail and
 * nothing centred; `centered` exists only for the final CTA, where a centred
 * ask is the deliberate exception.
 */
export function SectionHeading({ eyebrow, title, lede, centered = false, className, children }) {
  return (
    <div className={cx("rp-reveal", centered ? "mx-auto max-w-3xl text-center" : "max-w-3xl", className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="rp-h2 mt-5 text-[color:var(--rp-heading)]">{title}</h2>
      {lede && <p className={cx("rp-lede mt-6", centered && "mx-auto")}>{lede}</p>}
      {children}
    </div>
  );
}

// Focus is NOT declared here. index.css sets a global :focus-visible ring at
// 2px / 3px offset per rule focus.visible, so every control gets one whether
// or not its author remembered. A per-component ring would only override it
// with something less consistent.
const BUTTON_BASE =
  "inline-flex items-center justify-center rounded-[var(--radius-btn)] font-semibold " +
  "transition-colors duration-200 " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const BUTTON_SIZES = {
  sm: "h-9 px-4 text-[13px]",
  md: "h-11 px-6 text-sm",
  lg: "h-[52px] px-8 text-[15px]",
};

const BUTTON_VARIANTS = {
  // The accent. Lightens on hover, darkens on press — never a shadow.
  // Rule color.on-accent (block): text on the accent is pure #FFFFFF at
  // 4.55:1. The heading token only reaches 4.24:1 and fails AA.
  primary: "bg-brand text-white hover:bg-brand-400 active:bg-brand-600",
  // A hairline and nothing else. Hover raises the hairline's opacity.
  secondary:
    "border border-[color:var(--hairline-soft)] text-[color:var(--rp-heading)] " +
    "hover:border-[color:var(--hairline)] hover:text-white",
  // For use inside the one saturated CTA band, where the accent is the ground.
  onAccent: "bg-white text-[color:var(--rp-accent-press)] hover:bg-white/90",
};

/**
 * The one button. Renders an <a> for external links, a react-router <Link>
 * for internal paths, and a <button> when given onClick — so a CTA is never
 * a div pretending to be interactive.
 */
export function Button({
  to,
  href,
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}) {
  const classes = cx(BUTTON_BASE, BUTTON_SIZES[size], BUTTON_VARIANTS[variant], className);

  if (href) {
    return (
      <a className={classes} href={href} rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  }
  if (to) {
    return (
      <Link className={classes} to={to} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" className={classes} {...rest}>
      {children}
    </button>
  );
}

/**
 * A card. Surface tone, hairline, 16px radius, no shadow ever.
 *
 * `featured` spends the page's accent hairline. Exactly one card in a grid
 * should carry it — a second costs the first its meaning.
 */
export function Card({ as: Tag = "div", featured = false, className, children, ...rest }) {
  return (
    <Tag
      className={cx(
        "rounded-[var(--radius-card)] bg-ink-700 p-6 md:p-8 transition-colors duration-500",
        featured
          ? "border border-brand"
          : "border border-[color:var(--hairline-soft)] hover:border-[color:var(--hairline)]",
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** A big number with a small wide label under it. */
export function Stat({ value, label, className }) {
  return (
    <div className={cx("rp-reveal", className)}>
      <div className="rp-h3 font-semibold text-[color:var(--rp-heading)]">{value}</div>
      <div className="rp-eyebrow mt-2">{label}</div>
    </div>
  );
}

/**
 * The full-bleed accent band — "one saturated scene change per page, exactly
 * where conversion happens".
 *
 * Rendered at most once per page. If you find yourself wanting a second,
 * the first one is in the wrong place.
 */
export function CTABand({ eyebrow, title, body, children }) {
  return (
    <section className="rp-cta-band py-16 md:py-24">
      <Wrap className="max-w-3xl text-center">
        {/* On the accent band the ground is the accent itself, so the
            muted canvas token does not apply — white at 90% clears AA
            against it. */}
        {eyebrow && <p className="rp-eyebrow m-0 text-white/90">{eyebrow}</p>}
        <h2 className="rp-h2 mt-5 text-white">{title}</h2>
        {body && <p className="mt-6 text-[18px] leading-relaxed text-white">{body}</p>}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">{children}</div>
      </Wrap>
    </section>
  );
}

/**
 * Lists use arrows, never bullets. `→` is the system's one piece of
 * iconography and it already carries the transformation meaning the copy uses
 * it for.
 */
export function ArrowList({ items, className }) {
  return (
    <ul className={cx("m-0 list-none space-y-3 p-0", className)}>
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-sm leading-relaxed text-[color:var(--rp-body)]">
          <span aria-hidden="true" className="mt-px shrink-0 text-brand">
            →
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A hairline-separated FAQ. Native <details> so it works before hydration,
 * keeps keyboard behaviour for free, and is findable by in-page search — all
 * of which a JavaScript accordion gives up.
 */
export function FAQ({ items, className }) {
  return (
    <div className={cx("divide-y divide-[color:var(--hairline-soft)] border-y border-[color:var(--hairline-soft)]", className)}>
      {items.map((item) => (
        <details key={item.q} className="group py-5">
          <summary className="flex cursor-pointer list-none items-start justify-between gap-6 text-[18px] font-semibold text-[color:var(--rp-heading)] [&::-webkit-details-marker]:hidden">
            <span>{item.q}</span>
            <span
              aria-hidden="true"
              className="mt-1 shrink-0 text-brand transition-transform duration-300 group-open:rotate-90"
            >
              →
            </span>
          </summary>
          <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[color:var(--rp-body)]">
            {item.a}
          </p>
        </details>
      ))}
    </div>
  );
}
